import { Col, Form, Row, Space, Switch } from 'antd'
import CIMSSection from 'components/CIMSSection'
import { CIMSTags } from 'components/CIMSTags'
import { PDFDesignLayerType } from '../../types'
import LayerPreview from './components/LayerPreview'
import { TabContentFormItems, TabContentProps } from './types'
import { useForm, useWatch } from 'antd/lib/form/Form'
import { Permission, an } from '@cims-online/shared'
import CIMSBadge from 'components/CIMSBadge'
import CIMSButton from 'components/CIMSButton'
import CIMSFormItem from 'components/CIMSFormItem'
import CIMSSelect from 'components/CIMSSelect'
import { simpleEditNotification } from 'hooks/notificationHandler'
import { useCheckPermission } from 'hooks/useCheckPermission'
import { useDisabledOptions } from 'hooks/useDisabledOptions'
import {
  useEditPdfDesignLayerMutation,
  EditPdfDesignLayerSettingsInput,
} from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { usePDFDesignLayersContext } from '../../PDFDesignLayersContext'
import ProjectLayersInput from './components/ProjectLayerInput'
import { useOptions } from './hooks/useOptions'
import { useEffect, useState } from 'react'
import { useProjectContext } from 'context/ProjectContext'

/**
 * i'd love to be able to split this component up into form items + logic, and
 * preview + logic, but unfortunately, i couldn't figure out how to separate the
 * two without breaking the form or project layer ids in the preview
 */
const TabContent: React.VFC<TabContentProps> = ({ typename }) => {
  const { projectId } = useProjectContext()
  const { pdfDesignLayers, projectLayers } = usePDFDesignLayersContext()
  const options = useOptions(typename)
  const [form] = useForm<TabContentFormItems>()
  const settings = pdfDesignLayers.getSettings(typename)
  const useAll = useWatch('useAll', form)
  const globalId: string | undefined = useWatch('globalId', form)
  const projectLayerIds = useWatch('projectLayerIds', form)

  useEffect(() => {
    form.resetFields()
  }, [pdfDesignLayers, typename, projectId, form])
  useEffect(() => {
    if (!globalId) return
    form.setFieldValue(
      'projectLayerIds',
      settings?.recordSettings?.find(
        (settings) => settings.globalId.toLowerCase() === globalId.toLowerCase()
      )?.projectLayerIds ?? []
    )
  }, [form, globalId, settings?.recordSettings])

  const [mutate] = useEditPdfDesignLayerMutation({
    ...DefaultMutationHookOptions,
    onCompleted: () => simpleEditNotification('Settings'),
  })
  const [mutating, setMutating] = useState(false)
  const [commonDisabled] = useDisabledOptions([
    { disabled: projectLayers.loading, reason: 'loading' },
    {
      disabled: !projectLayers.data?.project.projectMetadata?.mapServerLayers,
      reason: { why: 'disabled', message: 'There are no Project Layers' },
    },
    { reason: 'loading', disabled: mutating },
  ])
  const [optionsDisabled, optionsDisabledState] = useDisabledOptions([
    { disabled: options[typename].loading, reason: 'loading' },
    {
      disabled: !options[typename].options.length,
      reason: {
        why: 'disabled',
        message: `There are no ${PDFDesignLayerType[typename].names} to select`,
      },
    },
  ])
  const initialValues: TabContentFormItems = {
    useAll: settings?.useAllLayers ?? false,
    globalId: undefined!,
    projectLayerIds: settings?.useAllLayers
      ? settings.projectLayerIds ?? []
      : [],
  }
  return (
    <Row justify="space-around" style={{ gap: 16 }}>
      <Col flex={1}>
        <CIMSSection title="Applicable Reports">
          <CIMSTags
            tags={PDFDesignLayerType[typename].reports.map(({ name }) => ({
              text: name,
            }))}
          />
        </CIMSSection>
        <CIMSSection title="Project Layers">
          <Form
            style={{ maxWidth: 500 }}
            form={form}
            initialValues={initialValues}
            onFinish={async ({ projectLayerIds, ...values }) => {
              const newSettings: EditPdfDesignLayerSettingsInput = {
                useAllLayers: values.useAll,
              }
              if (values.useAll) newSettings.projectLayerIds = projectLayerIds
              else {
                newSettings.recordSettings = {
                  upsert: true,
                  inputs: [{ globalId: values.globalId, projectLayerIds }],
                }
              }
              setMutating(true)
              await mutate({
                variables: {
                  input: {
                    index: PDFDesignLayerType[typename].featureServerId,
                    projectId,
                    settings: newSettings,
                  },
                },
              })
              setMutating(false)
              pdfDesignLayers.refetch()
            }}
          >
            <CIMSFormItem
              name="useAll"
              help={
                useAll
                  ? 'Use the configured project layers every time when generating composite images'
                  : `Use the configured project layers when an applicable report has the selected ${PDFDesignLayerType[typename].name}`
              }
              label={`Use with all ${PDFDesignLayerType[typename].names}`}
              valuePropName="checked"
            >
              <Switch
                onChange={(checked) =>
                  form.setFieldValue(
                    'projectLayerIds',
                    checked
                      ? settings?.projectLayerIds ?? []
                      : settings?.recordSettings?.find(
                          (settings) =>
                            settings.globalId.toLowerCase() ===
                            globalId?.toLowerCase()
                        )?.projectLayerIds ?? []
                  )
                }
              />
            </CIMSFormItem>
            {!useAll && (
              <CIMSFormItem
                disabled={optionsDisabled}
                label={
                  <CIMSBadge
                    count={settings?.recordSettings?.length ?? 0}
                    showZero
                  >
                    {PDFDesignLayerType[typename].name}
                  </CIMSBadge>
                }
                name="globalId"
              >
                <CIMSSelect
                  simple
                  options={options[typename].options}
                  loading={options[typename].loading}
                  disabled={optionsDisabledState.some}
                />
              </CIMSFormItem>
            )}
            <Space style={{ marginBottom: 8 }}>
              <CIMSBadge count={projectLayerIds?.length} showZero />
              <CIMSButton
                disabled={commonDisabled}
                onClick={() =>
                  form.setFieldValue(
                    'projectLayerIds',
                    (
                      projectLayers.data?.project.projectMetadata
                        ?.mapServerLayers ?? []
                    ).map(({ id }) => id)
                  )
                }
              >
                All
              </CIMSButton>
              <CIMSButton
                disabled={commonDisabled}
                onClick={() => form.setFieldValue('projectLayerIds', [])}
              >
                None
              </CIMSButton>
              <CIMSButton
                disabled={commonDisabled}
                onClick={() =>
                  form.setFieldValue(
                    'projectLayerIds',
                    useAll
                      ? settings?.projectLayerIds ?? []
                      : settings?.recordSettings?.find(
                          (recordSetting) =>
                            recordSetting.globalId.toLowerCase() ===
                            globalId?.toLowerCase()
                        )?.projectLayerIds ?? []
                  )
                }
              >
                Reset
              </CIMSButton>
              {useCheckPermission(Permission.EditPDFDesignLayer) && (
                <CIMSButton
                  disabled={[
                    ...commonDisabled,
                    {
                      reason: {
                        why: 'disabled',
                        message: `You must select ${an(
                          PDFDesignLayerType[typename].name
                        )}`,
                      },
                      disabled: !useAll && !globalId,
                    },
                  ]}
                  type="primary"
                  onClick={() => form.submit()}
                >
                  Save
                </CIMSButton>
              )}
            </Space>
            <CIMSFormItem name="projectLayerIds">
              <ProjectLayersInput />
            </CIMSFormItem>
          </Form>
        </CIMSSection>
      </Col>
      <Col flex={1}>
        <CIMSSection title="Preview">
          <LayerPreview
            typename={typename}
            projectLayerIds={projectLayerIds}
            globalId={useAll ? false : globalId}
          />
        </CIMSSection>
      </Col>
    </Row>
  )
}

export default TabContent
